import {auth} from "../firebase/firebase";

// Uploads banner previews to assets.remerge.io through the `bannerUpload`
// function, using the uploader's own AWS SSO (IAM Identity Center) login.
// The SSO access token only lives in memory, so a page reload asks again.

// Override for local testing against the Functions emulator, e.g.
// REACT_APP_BANNER_UPLOAD_URL=http://127.0.0.1:5001/remerge-se/europe-west1/bannerUpload
const baseURL = process.env.REACT_APP_BANNER_UPLOAD_URL
    || "https://europe-west1-remerge-se.cloudfunctions.net/bannerUpload";

export type BannerUploadType = "preview" | "newPreview" | "raw";

export class BannerUploadError extends Error {
    constructor(readonly code: string, message: string) {
        super(message);
    }
}

interface SsoSession {
    accessToken: string;
    expiresAt: number;
}

let ssoSession: SsoSession | undefined;

async function post<T>(path: string, body: object, headers: Record<string, string> = {}): Promise<T> {
    const user = auth.currentUser;
    if (!user) {
        throw new BannerUploadError("unauthenticated", "Please sign in to the Template Manager first");
    }
    const resp = await fetch(baseURL + path, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            "Authorization": "Bearer " + await user.getIdToken(),
            ...headers,
        },
        body: JSON.stringify(body),
    });
    const data = await resp.json().catch(() => ({}));
    if (!resp.ok) {
        throw new BannerUploadError(data.error || `http_${resp.status}`, data.message || resp.statusText);
    }
    return data as T;
}

const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

interface LoginStart {
    deviceCode: string;
    verificationUriComplete: string;
    userCode: string;
    expiresIn: number;
    interval: number;
}

type LoginPoll =
    | { status: "pending" | "slow_down" | "expired" | "denied" }
    | { status: "ok"; accessToken: string; expiresAt: number };

export interface SsoPrompt {
    verificationUri: string;
    userCode: string;
    popupBlocked: boolean;
}

// `popup` must be opened synchronously in the click handler, otherwise the
// browser blocks it; it is pointed at the SSO approval page once we have it.
async function loginWithSso(popup: Window | null, onPrompt: (p: SsoPrompt) => void): Promise<string> {
    const start = await post<LoginStart>("/login/start", {});
    if (popup && !popup.closed) {
        popup.location.href = start.verificationUriComplete;
    }
    onPrompt({
        verificationUri: start.verificationUriComplete,
        userCode: start.userCode,
        popupBlocked: !popup || popup.closed,
    });

    let interval = start.interval * 1000;
    const deadline = Date.now() + start.expiresIn * 1000;
    try {
        while (Date.now() < deadline) {
            await sleep(interval);
            const poll = await post<LoginPoll>("/login/poll", {deviceCode: start.deviceCode});
            switch (poll.status) {
                case "ok":
                    ssoSession = {accessToken: poll.accessToken, expiresAt: poll.expiresAt};
                    return poll.accessToken;
                case "slow_down":
                    interval += 5000;
                    break;
                case "denied":
                    throw new BannerUploadError("sso_denied", "AWS SSO login was denied");
                case "expired":
                    throw new BannerUploadError("sso_expired", "AWS SSO login timed out, please try again");
            }
        }
        throw new BannerUploadError("sso_expired", "AWS SSO login timed out, please try again");
    } finally {
        if (popup && !popup.closed) {
            popup.close();
        }
    }
}

function validSsoToken(): string | undefined {
    // Leave a minute of slack so the token does not expire mid-upload.
    if (ssoSession && ssoSession.expiresAt - 60 * 1000 > Date.now()) {
        return ssoSession.accessToken;
    }
    return undefined;
}

export function needsSsoLogin(): boolean {
    return !validSsoToken();
}

export async function uploadBanner(
    s3Folder: string,
    type: BannerUploadType,
    body: string,
    popup: Window | null,
    onPrompt: (p: SsoPrompt) => void,
): Promise<{ key: string; url: string }> {
    let token = validSsoToken();
    if (!token) {
        token = await loginWithSso(popup, onPrompt);
    } else if (popup && !popup.closed) {
        popup.close();
    }
    try {
        return await post("/upload", {s3Folder, type, body}, {"X-Aws-Sso-Token": token});
    } catch (e) {
        if (e instanceof BannerUploadError && e.code === "sso_login_required") {
            ssoSession = undefined;
        }
        throw e;
    }
}
